import {ExpressionPath} from '@packages/systems/wfdl';
import {TEMP_PATH} from '@packages/utilities/expression-path-constants';

const SIZE_ERR = 'SIZE_ERROR';
const TYPE_ERR = 'TYPE_ERROR';
const GENERIC_ERR = 'GENERIC_ERROR';

export type FileUploadErrorType = {
  id: string;
  msg: string;
  path: Array<string>;
};

export const FILE_UPLOAD_ERRORS = {
  SIZE: {
    id: SIZE_ERR,
    msg: 'Upload failed. Max size for files is 10 MB.',
    path: ['data', 'form', SIZE_ERR],
  },
  TYPE: {
    id: TYPE_ERR,
    msg: 'Upload failed. Invalid file type.',
    path: ['data', 'form', TYPE_ERR],
  },
  GENERIC: {
    id: GENERIC_ERR,
    msg: 'Upload failed. Something went wrong. Please retry.',
    path: ['data', 'form', GENERIC_ERR],
  },
} as const;

// check more information about proper hiding default input and accessibility
// https://blog.bitsrc.io/customise-radio-buttons-without-compromising-accessibility-b03061b5ba93
export const HIDE_DEFAULT_INPUT_STYLES = {
  opacity: 0,
  position: 'absolute',
  zIndex: -1,
} as const;

export const TEXT_TYPE_LABELS = {
  text: 'Plain',
  email: 'Email',
  password: 'Password',
  tel: 'Phone',
  number: 'Number',
  url: 'URL',
} as const;

export const CHECKED_CLASS = 'w--redirected-checked';
export const FOCUSED_CLASS = 'w--redirected-focus';
export const FOCUSED_VISIBLE_CLASS = 'w--redirected-focus-visible';

// Default values for Form elements
export const FORM_DEFAULT_LABEL = 'field';
export const FILE_DEFAULT_LABEL = 'file';
export const FILE_DEFAULT_LABEL_CAP = 'File';
export const RADIO_DEFAULT_VALUE = 'radio';
export const RADIO_DEFAULT_VALUE_CAP = 'Radio';
export const EMAIL_FORM_DEFAULT = 'email-form';
export const CHECKBOX_DEFAULT = 'checkbox';
export const CHECKBOX_DEFAULT_CAP = 'Checkbox';

// Paths for Form elements
const ATTR_PATH: ExpressionPath = [{in: 'Record', at: 'attr'}];
const FORM_PATH: ExpressionPath = [{in: 'Record', at: 'form'}];
export const SOURCE_PATH: ExpressionPath = [{in: 'Record', at: 'Source'}];

export const LOGIC_FLOW_ID_PATH: ExpressionPath = [
  ...SOURCE_PATH,
  {in: 'Union', tag: 'Logic'},
  {in: 'Record', at: 'flowId'},
];
export const INPUT_TYPE_PATH: ExpressionPath = [
  {in: 'Record', at: 'inputType'},
];
export const FORM_NAME_PATH: ExpressionPath = [
  ...FORM_PATH,
  {in: 'Record', at: 'name'},
];
export const FORM_OPTS_PATH: ExpressionPath = [
  ...FORM_PATH,
  {in: 'Record', at: 'opts'},
];
export const FORM_PASSWORDPAGE_PATH: ExpressionPath = [
  ...FORM_PATH,
  {in: 'Record', at: 'passwordPage'},
];
export const FORM_SIZE_ERROR_PATH: ExpressionPath = [
  ...FORM_PATH,
  {in: 'Record', at: SIZE_ERR},
];
export const FORM_TYPE_ERROR_PATH: ExpressionPath = [
  ...FORM_PATH,
  {in: 'Record', at: TYPE_ERR},
];
export const FORM_GENERIC_ERROR_PATH: ExpressionPath = [
  ...FORM_PATH,
  {in: 'Record', at: GENERIC_ERR},
];
export const FORM_MANAGE_PATH: ExpressionPath = [
  ...FORM_PATH,
  {in: 'Record', at: 'manageTypes'},
];
export const FORM_DOCUMENTS_PATH: ExpressionPath = [
  ...FORM_PATH,
  {in: 'Record', at: 'acceptDocuments'},
];
export const FORM_IMAGES_PATH: ExpressionPath = [
  ...FORM_PATH,
  {in: 'Record', at: 'acceptImages'},
];
export const FORM_CUSTOM_PATH: ExpressionPath = [
  ...FORM_PATH,
  {in: 'Record', at: 'acceptCustom'},
];
export const FORM_CUSTOM_TYPES_PATH: ExpressionPath = [
  ...FORM_PATH,
  {in: 'Record', at: 'customTypes'},
];
export const ATTR_REQUIRED_PATH: ExpressionPath = [
  ...ATTR_PATH,
  {in: 'Record', at: 'required'},
];
export const ATTR_NAME_PATH: ExpressionPath = [
  ...ATTR_PATH,
  {in: 'Record', at: 'name'},
];
export const ATTR_ID_PATH: ExpressionPath = [
  ...ATTR_PATH,
  {in: 'Record', at: 'id'},
];
export const ATTR_ACCEPT_PATH: ExpressionPath = [
  ...ATTR_PATH,
  {in: 'Record', at: 'accept'},
];
export const ATTR_VALUE_PATH: ExpressionPath = [
  ...ATTR_PATH,
  {in: 'Record', at: 'value'},
];
export const ATTR_CHECKED_PATH: ExpressionPath = [
  ...ATTR_PATH,
  {in: 'Record', at: 'checked'},
];
export const ATTR_MULTIPLE_PATH: ExpressionPath = [
  ...ATTR_PATH,
  {in: 'Record', at: 'multiple'},
];
export const ATTR_DATA_NAME_PATH: ExpressionPath = [
  ...ATTR_PATH,
  {in: 'Record', at: 'data-name'},
];

export const ATTR_PLACEHOLDER_PATH: ExpressionPath = [
  ...ATTR_PATH,
  {in: 'Record', at: 'placeholder'},
];
export const ATTR_TYPE_PATH: ExpressionPath = [
  ...ATTR_PATH,
  {in: 'Record', at: 'type'},
];
export const ATTR_AUTOFOCUS_PATH: ExpressionPath = [
  ...ATTR_PATH,
  {in: 'Record', at: 'autofocus'},
];
export const REDIRECT_PATH: ExpressionPath = [
  ...ATTR_PATH,
  {in: 'Record', at: 'redirect'},
];
export const DATA_REDIRECT_PATH: ExpressionPath = [
  ...ATTR_PATH,
  {in: 'Record', at: 'data-redirect'},
];
export const CLOSE_MODAL_PATH: ExpressionPath = [
  ...ATTR_PATH,
  {in: 'Record', at: 'data-wf-close-modal'},
];
export const ID_PATH: ExpressionPath = [...ATTR_PATH, {in: 'Record', at: 'id'}];
export const ACTION_PATH: ExpressionPath = [
  ...ATTR_PATH,
  {in: 'Record', at: 'action'},
];
export const METHOD_PATH: ExpressionPath = [
  ...ATTR_PATH,
  {in: 'Record', at: 'method'},
];

export const TEMP_PASSWORD_STATE_PATH: ExpressionPath = [
  ...TEMP_PATH,
  {in: 'Record', at: 'password-state'},
];

// Form File Upload file types supported
export const SUPPORTED_FORM_UPLOAD_DOCUMENT_TYPES = [
  'ai',
  'doc',
  'docx',
  'indd',
  'key',
  'numbers',
  'pps',
  'ppt',
  'pptx',
  'psd',
  'ods',
  'odt',
  'odp',
  'pages',
  'pdf',
  'txt',
  'xls',
  'xlsx',
] as const;

export const SUPPORTED_FORM_UPLOAD_IMAGE_TYPES = [
  'bmp',
  'dng',
  'eps',
  'gif',
  'jpg',
  'jpeg',
  'png',
  'ps',
  'raw',
  'svg',
  'tga',
  'tif',
  'tiff',
] as const;

export const FORBIDDEN_FORM_UPLOAD_TYPES = [
  'ade',
  'adp',
  'apk',
  'app',
  'bat',
  'cab',
  'chm',
  'cgi',
  'cmd',
  'com',
  'cpl',
  'dll',
  'dmg',
  'exe',
  'hta',
  'html',
  'htm',
  'ins',
  'isp',
  'php',
  'phps',
  'phtml',
  'php3',
  'php4',
  'php5',
  'pkg',
  'ps1',
  'gadget',
  'jar',
  'js',
  'jse',
  'lib',
  'lnk',
  'mde',
  'msc',
  'msi',
  'msp',
  'mst',
  'nsh',
  'pif',
  'src',
  'sct',
  'shb',
  'sys',
  'sh',
  'vb',
  'vbe',
  'vbs',
  'vxd',
  'wsc',
  'wsf',
  'wsh',
  'tar',
  'tar.gz',
] as const;

export const IANA_TYPES = ['image/*', 'audio/*', 'video/*'] as const;

export type FormTextInputType = keyof typeof TEXT_TYPE_LABELS;
